/**
 * Tabs with an underline (cockatrice-ui `src/ui/Tabs.jsx`, ported to hyperscript; PLAN.md §0).
 * tabs: [{ value, label, count?, disabled?, content? }].
 * Controlled with `value` + `onChange`, or uncontrolled with `defaultValue`.
 * Arrow keys move and select (automatic activation), Home/End jump to the ends.
 * If no tab has `content`, only the tablist renders and you show panels yourself: name them with `panelId` (one id for
 * every tab, or a function of the tab's value) so each tab's aria-controls points at them, or use `children` as a
 * render function.
 */
import './css/tabs.css';
import { createSignal, createUniqueId, mergeProps, splitProps } from 'solid-js';
import { each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface TabItem {
  /** Unique value. */
  readonly value: string;
  /** Visible label. */
  readonly label: Child;
  /** Optional count shown in a pill. */
  readonly count?: number | string | undefined;
  /** Not selectable. */
  readonly disabled?: boolean | undefined;
  /** Panel content for this tab. */
  readonly content?: Child;
}

export interface TabsProps extends Attributes {
  /** The tabs. */
  readonly tabs: readonly TabItem[];
  /** Selected tab (controlled). */
  readonly value?: string | undefined;
  /** Initial tab (uncontrolled). Default: first tab. */
  readonly defaultValue?: string | undefined;
  /** Called with the new value. */
  readonly onChange?: ((value: string) => void) | undefined;
  /** Accessible name of the tablist. Required. */
  readonly label: string;
  /** Size. Default "md". */
  readonly size?: 'md' | 'sm' | undefined;
  /** Tabs share the width equally. */
  readonly fill?: boolean | undefined;
  /** Renders the selected panel instead of tab.content. */
  readonly children?: ((value: string) => Child) | undefined;
  /** Id prefix for tabs and panels. */
  readonly id?: string | undefined;
  /**
   * The panel the page renders itself (Tabs renders none): its id, one for every tab, or the id for a tab's value.
   * Sets each tab's aria-controls. Ignored when Tabs renders the panels.
   */
  readonly panelId?: string | ((value: string) => string) | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

export default function Tabs(props: TabsProps): Child {
  const [local, rest] = splitProps(props, ['class', 'tabs', 'value', 'defaultValue', 'onChange', 'label', 'size', 'fill', 'children', 'id', 'panelId']);
  const base = local.id ?? createUniqueId();
  const [inner, setInner] = createSignal(local.defaultValue ?? local.tabs[0]?.value);
  const current = (): string | undefined => (local.value ?? inner());
  const select = (v: string): void => {
    if (local.value === undefined) setInner(v);
    local.onChange?.(v);
  };
  const enabled = (): readonly TabItem[] => local.tabs.filter((t) => t.disabled !== true);
  let list: HTMLDivElement | undefined;

  const onKey = (e: KeyboardEvent): void => {
    const tabs = enabled();
    const i = tabs.findIndex((t) => t.value === current());
    let next: TabItem | undefined;
    if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
    else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
    else if (e.key === 'Home') next = tabs[0];
    else if (e.key === 'End') next = tabs[tabs.length - 1];
    if (next === undefined) return;
    e.preventDefault();
    select(next.value);
    const tab = list?.querySelector(`#${CSS.escape(`${base}-tab-${next.value}`)}`);
    if (tab instanceof HTMLElement) tab.focus();
  };

  const hasPanels = (): boolean => local.tabs.some((t) => t.content !== undefined && t.content !== null) || typeof local.children === 'function';
  /** The panel a tab controls: Tabs' own, else the one the page names (none: no aria-controls). */
  const controls = (value: string): string | undefined => {
    if (hasPanels()) return `${base}-panel-${value}`;
    const named = local.panelId;
    return typeof named === 'function' ? named(value) : named;
  };

  return el(
    'div',
    mergeProps({ class: () => ['ui-tabs', local.class].filter(Boolean).join(' ') }, rest),
    el(
      'div',
      {
        ref: (node: HTMLDivElement) => {
          list = node;
        },
        class: () => ['ui-tabs__list', local.size === 'sm' && 'ui-tabs__list--sm', local.fill === true && 'ui-tabs__list--fill'].filter(Boolean).join(' '),
        role: 'tablist',
        'aria-label': () => local.label,
        onKeyDown: onKey,
      },
      each(
        () => local.tabs,
        (t) => {
          const on = (): boolean => t.value === current();
          return el(
            'button',
            {
              type: 'button',
              role: 'tab',
              id: () => `${base}-tab-${t.value}`,
              class: 'ui-tabs__tab',
              'aria-selected': () => (on() ? 'true' : 'false'),
              'aria-controls': () => controls(t.value),
              tabindex: () => (on() ? 0 : -1),
              disabled: () => t.disabled,
              onClick: () => {
                select(t.value);
              },
            },
            () => t.label,
            when(
              () => t.count !== undefined,
              () => el('span', { class: 'ui-tabs__count' }, () => t.count),
            ),
          );
        },
      ),
    ),
    when(hasPanels, () =>
      each(
        () => local.tabs,
        (t) =>
          el(
            'div',
            {
              class: 'ui-tabs__panel',
              role: 'tabpanel',
              id: () => `${base}-panel-${t.value}`,
              'aria-labelledby': () => `${base}-tab-${t.value}`,
              tabindex: '0',
              hidden: () => t.value !== current(),
            },
            when(
              () => t.value === current(),
              () => {
                const render = local.children;
                return typeof render === 'function' ? render(t.value) : t.content;
              },
            ),
          ),
      ),
    ),
  );
}
